// Landing hero + scroll story: the carbon map's real 4 × 4 km in 3D. The hero shows the whole
// area (every parcel of the 2030 land-use plan); scrolling turns the stage into the real Bản đồ
// carbon UI (./world/RealUi — the app's own components) and plays its flow on one coffee parcel:
// sync, pick, the NDVI scan, imagery 2017/2020/2026 and EUDR, carbon and yield, the 3D twin. Same
// engine and baked data as /app/carbon-map. Reduced motion or no WebGL → a still hero and the same
// readings as a plain list.
import './world/world.css'
import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform, type MotionValue } from 'framer-motion'
import { useI18n, type Lang } from '../lib/i18n'
import { Button } from '../components/Button'
import { Countdown } from '../components/Countdown'
import { TelemetryStrip } from '../components/TelemetryStrip'
import { ease } from '../lib/motion'
import { STORY, type Win } from './world/story'
import type { HeroFacts, World } from './world/worldData'

const WorldScene = lazy(() => import('./world/WorldScene'))
const RealUi = lazy(() => import('./world/RealUi'))

const hasWebGL = () => {
  try {
    const c = document.createElement('canvas')
    return !!(c.getContext('webgl2') || c.getContext('webgl'))
  } catch {
    return false
  }
}

/* ------------------------------------------------------------ numbers */
type Y3<T> = [T, T, T]
interface Facts {
  parcels: number
  totalHa: number
  kmW: number
  kmD: number
  centre: [number, number]
  heroId: number
  to: number
  thua: number
  ha: number
  dates: Y3<string>
  ndvi: Y3<number | null> | null
  pass: boolean | null
  lossPct: number
  trees: number | null
  tallPct: number | null
  co2: number | null
  split: { shade: number; coffee: number; root: number } | null
  yield: { p10: number; p50: number; p90: number } | null
}

// Baked values of the demo area, shown until the data files arrive (same numbers, no layout jump).
const BAKED: Facts = {
  parcels: 555, totalHa: 798.6, kmW: 4.0, kmD: 4.1, centre: [108.1715, 12.7035],
  heroId: 35030, to: 35, thua: 30, ha: 1.65, dates: ['2017-02-07', '2020-03-13', '2026-03-27'],
  ndvi: null, pass: null, lossPct: 0, trees: null, tallPct: null, co2: null, split: null, yield: null,
}

function derive(world: World | null, facts: HeroFacts | null): Facts {
  if (!world) return BAKED
  const m = world.meta
  const b = m.bbox
  const sc = (y: number) => m.s2.scenes.find((s) => s.year === y)?.date ?? ''
  const a = facts?.analysis
  return {
    parcels: m.parcels.count,
    totalHa: m.parcels.totalHa,
    kmW: (world.size.w * 10) / 1000,
    kmD: (world.size.d * 10) / 1000,
    centre: [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2],
    heroId: world.hero.id,
    to: world.hero.props.to,
    thua: world.hero.props.thua,
    ha: world.hero.props.a / 10000,
    dates: [sc(2017), sc(2020), sc(2026)],
    ndvi: a ? [a.ndvi[2017], a.ndvi[2020], a.ndvi[2026]] : null,
    pass: a ? a.eudr.pass : null,
    lossPct: a ? a.eudr.lossPct : 0,
    trees: a ? a.trees.length : null,
    tallPct: a ? a.tallCover * 100 : null,
    co2: facts ? facts.stock.totalT : null,
    split: facts ? { shade: facts.stock.shadeT, coffee: facts.stock.coffeeT, root: facts.stock.rootT } : null,
    yield: facts?.yieldF.applicable ? { p10: facts.yieldF.p10, p50: facts.yieldF.p50, p90: facts.yieldF.p90 } : null,
  }
}

const num = (lang: Lang, v: number | null | undefined, digits = 0) =>
  v == null || !Number.isFinite(v) ? '…' : v.toLocaleString(lang === 'vi' ? 'vi-VN' : 'en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits })
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const day = (lang: Lang, iso: string) => {
  const [y, mo, d] = iso.split('-').map(Number)
  if (!y) return '…'
  return lang === 'vi' ? `${String(d).padStart(2, '0')}/${String(mo).padStart(2, '0')}/${y}` : `${d} ${MONTHS[mo - 1]} ${y}`
}

/* --------------------------------------------------------------- copy */
function copyFor(lang: Lang, f: Facts) {
  const n = (v: number | null | undefined, dg = 0) => num(lang, v, dg)
  const area = `${n(f.kmW, 1)} × ${n(f.kmD, 1)} km`
  if (lang === 'en')
    return {
      aria: 'ARGO carbon map: one parcel read from satellite data',
      h1a: 'Every parcel,',
      h1b: 'evidence read from orbit.',
      sub: `This is a real ${area} area east of Buôn Ma Thuột, Đắk Lắk: ${n(f.parcels)} perennial-crop parcels from the 2030 land-use plan, rebuilt from Sentinel-2 imagery, elevation and canopy height.`,
      cta1: 'Open the carbon map',
      cta2: 'Launch ARGO console',
      cue: 'Scroll to watch ARGO read one parcel',
      pick: {
        title: 'Pick one coffee parcel.',
        body: `Its boundary comes from the 2030 land-use plan: ${n(f.ha, 2)} ha in Ea Knuếc commune. ARGO reads the imagery, terrain and canopy height inside that boundary only.`,
      },
      years: {
        title: 'Three dry seasons, three satellite images.',
        body: `Sentinel-2 imaged this parcel on ${day(lang, f.dates[0])}, ${day(lang, f.dates[1])} and ${day(lang, f.dates[2])}. Tree cover before and after the EUDR cutoff of 31 Dec 2020 is compared.`,
        verdict: f.pass === false ? `Tree-cover loss after 2020: ${n(f.lossPct, 1)}%` : 'No tree-cover loss after 31 Dec 2020',
      },
      trees: {
        rest: 'trees taller than 6 m.',
        body: `Each crown is counted on Meta and WRI's 1 m canopy-height map (2018–2020 imagery): shade and fruit trees intercropped with the coffee. ${n(f.tallPct)}% of the parcel has canopy 5 m or taller.`,
      },
      carbon: {
        title: `About ${n(f.co2)} tonnes of CO₂e stored in this parcel.`,
        body: 'A screening estimate from IPCC factors and the Chave 2014 equation, not a carbon credit.',
        yield: f.yield ? `Forecast green-coffee yield for 2026/27: ${n(f.yield.p50, 1)} t/ha, range ${n(f.yield.p10, 1)}–${n(f.yield.p90, 1)}.` : '',
      },
      outro: {
        title: 'That was one parcel.',
        body: `The carbon map holds all ${n(f.parcels)} of them. Pick any parcel to see its imagery over the years, tree count, carbon and yield.`,
        cta1: 'Open this parcel in the carbon map',
        cta2: 'Launch ARGO console',
      },
      still: 'One parcel, read from satellite data',
    }
  return {
    aria: 'Bản đồ carbon ARGO: đọc một thửa đất từ dữ liệu vệ tinh',
    h1a: 'Mỗi thửa đất,',
    h1b: 'một bằng chứng đọc từ vệ tinh.',
    sub: `Đây là một vùng ${area} thật ở phía đông Buôn Ma Thuột, Đắk Lắk: ${n(f.parcels)} thửa cây lâu năm theo quy hoạch 2030, dựng lại từ ảnh Sentinel-2, mô hình độ cao và bản đồ chiều cao tán cây.`,
    cta1: 'Mở bản đồ carbon',
    cta2: 'Vào hệ thống ARGO',
    cue: 'Cuộn xuống để xem ARGO đọc một thửa',
    pick: {
      title: 'Chọn một thửa cà phê.',
      body: `Ranh thửa lấy từ bản đồ quy hoạch sử dụng đất 2030: ${n(f.ha, 2)} ha ở xã Ea Knuếc. ARGO chỉ đọc ảnh, địa hình và chiều cao tán nằm trong ranh này.`,
    },
    years: {
      title: 'Ba mùa khô, ba ảnh vệ tinh.',
      body: `Sentinel-2 chụp thửa này ngày ${day(lang, f.dates[0])}, ${day(lang, f.dates[1])} và ${day(lang, f.dates[2])}. ARGO so độ phủ cây trước và sau mốc 31/12/2020 của EUDR.`,
      verdict: f.pass === false ? `Mất độ phủ cây sau 2020: ${n(f.lossPct, 1)}%` : 'Không mất độ phủ cây sau 31/12/2020',
    },
    trees: {
      rest: 'cây cao trên 6 m.',
      body: `Mỗi tán được đếm trên bản đồ chiều cao tán 1 m của Meta và WRI (ảnh 2018–2020): cây che bóng, cây ăn trái trồng xen trong vườn cà phê. ${n(f.tallPct)}% diện tích thửa có tán cao từ 5 m.`,
    },
    carbon: {
      title: `Khoảng ${n(f.co2)} tấn CO₂e đang nằm trong thửa.`,
      body: 'Ước tính sàng lọc theo hệ số IPCC và phương trình Chave 2014, chưa phải tín chỉ carbon.',
      yield: f.yield ? `Năng suất cà phê nhân dự báo vụ 2026/27: ${n(f.yield.p50, 1)} t/ha, khoảng ${n(f.yield.p10, 1)}–${n(f.yield.p90, 1)}.` : '',
    },
    outro: {
      title: 'Đó mới là một thửa.',
      body: `Bản đồ carbon có đủ ${n(f.parcels)} thửa như thế. Chọn thửa bất kỳ để xem ảnh qua các năm, số cây, carbon và năng suất.`,
      cta1: 'Mở thửa này trên bản đồ carbon',
      cta2: 'Vào hệ thống ARGO',
    },
    still: 'Một thửa, đọc từ dữ liệu vệ tinh',
  }
}

/* ----------------------------------------------------------- pieces */
/** A caption that fades through its window of the scroll story. */
function Step({ p, win, className, children }: { p: MotionValue<number>; win: Win; className?: string; children: ReactNode }) {
  const o = useTransform(p, [...win], [0, 1, 1, 0])
  const y = useTransform(p, [...win], [30, 0, 0, -22])
  const filter = useTransform(o, (v) => `blur(${((1 - v) * 6).toFixed(2)}px)`)
  const visibility = useTransform(o, (v) => (v < 0.02 ? 'hidden' : 'visible'))
  return (
    <motion.div className={`cw-step ${className ?? ''}`} style={{ opacity: o, y, filter, visibility }}>
      {children}
    </motion.div>
  )
}

/** the carbon map's own breakpoint: top bar, map and panel stack below 900 px */
function usePhone() {
  const q = '(max-width: 900px)'
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches)
  useEffect(() => {
    const mq = window.matchMedia(q)
    const on = () => setPhone(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return phone
}

/* ------------------------------------------------------------ section */
export function CarbonWorld() {
  const { lang } = useI18n()
  const reduced = !!useReducedMotion()
  const [webgl] = useState(() => typeof window !== 'undefined' && hasWebGL())
  const story = webgl && !reduced
  const phone = usePhone()
  const ref = useRef<HTMLElement>(null)
  const progress = useRef(0)
  const inApp = useRef(false)
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ['start start', 'end end'] })
  const [world, setWorld] = useState<World | null>(null)
  const [facts, setFacts] = useState<HeroFacts | null>(null)
  const [ready, setReady] = useState(false)
  const [visible, setVisible] = useState(true)
  useMotionValueEvent(p, 'change', (v) => {
    progress.current = v
    // while the real app UI plays, the page's own nav steps aside for the app's top bar
    const on = story && v > STORY.app[0] && v < STORY.app[3]
    if (on !== inApp.current) {
      inApp.current = on
      document.documentElement.toggleAttribute('data-cw-app', on)
    }
  })
  useEffect(() => () => document.documentElement.removeAttribute('data-cw-app'), [])

  useEffect(() => {
    let alive = true
    import('./world/worldData')
      .then(async (m) => {
        const w = await m.loadWorld()
        if (!alive) return
        setWorld(w)
        const hf = await m.loadHeroFacts(w.hero)
        if (alive) setFacts(hf)
      })
      .catch((e) => console.warn('[carbon world]', e))
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { rootMargin: '160px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const f = useMemo(() => derive(world, facts), [world, facts])
  const C = copyFor(lang, f)
  const heroO = useTransform(p, [...STORY.heroOut], [1, 0])
  const heroY = useTransform(p, [...STORY.heroOut], [0, -36])
  const heroVis = useTransform(heroO, (v) => (v < 0.02 ? 'hidden' : 'visible'))
  const outroO = useTransform(p, [STORY.app[2], STORY.outro[0]], [0, 1])

  const intro = (delay: number) =>
    reduced
      ? { initial: false as const }
      : { initial: { opacity: 0, y: 22 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.75, ease: ease.out as unknown as number[], delay } }

  const telemetry = [
    `${f.centre[1].toFixed(3)}°N ${f.centre[0].toFixed(3)}°E`,
    `${f.kmW.toFixed(1)} × ${f.kmD.toFixed(1)} KM`,
    `${f.parcels} CLN · ${f.totalHa.toFixed(1)} HA`,
    'SENTINEL-2 · 10 M',
    'CHM 1 M · META/WRI',
  ]

  const readings = (
    <ol className="cw-still-list">
      <li><h3>{C.pick.title}</h3><p>{C.pick.body}</p></li>
      <li><h3>{C.years.title}</h3><p>{C.years.body}</p><p className="cw-tone--pass">{C.years.verdict}</p></li>
      <li><h3>{num(lang, f.trees)} {C.trees.rest}</h3><p>{C.trees.body}</p></li>
      <li><h3>{C.carbon.title}</h3><p>{C.carbon.body}</p>{C.carbon.yield && <p>{C.carbon.yield}</p>}</li>
    </ol>
  )

  return (
    <section ref={ref} id="top" className={`cw ${story ? 'cw--story' : 'cw--still'}`} aria-label={C.aria}>
      <div className="cw-stage">
        <div className={`cw-world ${ready || !webgl ? 'is-ready' : ''}`} aria-hidden>
          {webgl && world && (
            <Suspense fallback={null}>
              <WorldScene world={world} facts={facts} progress={progress} still={!story} active={visible} onReady={() => setReady(true)} />
            </Suspense>
          )}
          {!webgl && <img className="cw-poster" src="/carbon-map/s2_2026_rgb.jpg" alt="" />}
        </div>
        <motion.div className="cw-scrim cw-scrim--hero" style={story ? { opacity: heroO } : undefined} aria-hidden />
        {story && <motion.div className="cw-scrim cw-scrim--story" style={{ opacity: outroO }} aria-hidden />}

        <motion.div className="cw-layer cw-hero" style={story ? { opacity: heroO, y: heroY, visibility: heroVis } : undefined}>
          <div className="container cw-hero-in">
            <div className="cw-hero-copy">
              <h1 className="cw-h1">
                <motion.span className="cw-h1-line" {...intro(0.15)}>{C.h1a}</motion.span>
                <motion.span className="cw-h1-line cw-h1-line--lo" {...intro(0.27)}>{C.h1b}</motion.span>
              </h1>
              <motion.p className="cw-sub" {...intro(0.42)}>{C.sub}</motion.p>
              <motion.div className="cw-actions" {...intro(0.55)}>
                <Button variant="primary" href="/app/carbon-map">{C.cta1}</Button>
                <Button variant="ghost" href="/app">{C.cta2}</Button>
              </motion.div>
              <motion.div className="cw-countdown" {...intro(0.7)}>
                <Countdown />
              </motion.div>
            </div>
          </div>
          <div className="container cw-hero-foot">
            <TelemetryStrip className="cw-telemetry" items={telemetry} />
            {story && (
              <span className="cw-cue">
                <span className="cw-cue-line" aria-hidden />
                {C.cue}
              </span>
            )}
          </div>
        </motion.div>

        {story && world && facts && (
          <Suspense fallback={null}>
            <RealUi world={world} facts={facts} p={p} phone={phone} />
          </Suspense>
        )}

        {story && (
          <div className="cw-layer cw-story">
            <div className="cw-sr">
              <h2>{C.still}</h2>
              {readings}
            </div>
            <div className="container cw-story-in">
              <Step p={p} win={[STORY.outro[0], STORY.outro[1], 2, 3]} className="cw-step--outro">
                <h2 className="cw-title">{C.outro.title}</h2>
                <p className="cw-body">{C.outro.body}</p>
                <div className="cw-actions">
                  <Button variant="primary" href={`/app/carbon-map?thua=${f.heroId}`}>{C.outro.cta1}</Button>
                  <Button variant="ghost" href="/app">{C.outro.cta2}</Button>
                </div>
              </Step>
            </div>
          </div>
        )}
      </div>

      {!story && (
        <div className="container cw-still">
          <h2 className="cw-still-title">{C.still}</h2>
          {readings}
          <div className="cw-actions">
            <Button variant="primary" href={`/app/carbon-map?thua=${f.heroId}`}>{C.outro.cta1}</Button>
          </div>
        </div>
      )}
    </section>
  )
}
